import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Trash2, Upload, FileText } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface BudgetFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budget?: any;
  onSuccess?: () => void;
}

interface BudgetItem {
  reference?: string;
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export function BudgetFormDialog({ open, onOpenChange, budget, onSuccess }: BudgetFormDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [nextNumber, setNextNumber] = useState<number | null>(null);
  const [items, setItems] = useState<BudgetItem[]>([]);
  const [sourceTab, setSourceTab] = useState<string>('manual');

  const form = useForm({
    defaultValues: {
      title: '',
      customer_id: '',
      project_id: '',
      status: 'draft',
      source: 'manual',
      subtotal: 0,
      tax_rate: 21,
      tax_amount: 0,
      total_amount: 0,
      issue_date: new Date().toISOString().split('T')[0],
      valid_until: '',
      perseo_reference: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      fetchCustomers();
      fetchProjects();

      if (budget) {
        form.reset({
          title: budget.title || '',
          customer_id: budget.customer_id || '',
          project_id: budget.project_id || '',
          status: budget.status || 'draft',
          source: budget.source || 'manual',
          subtotal: budget.subtotal || 0,
          tax_rate: budget.tax_rate || 21,
          tax_amount: budget.tax_amount || 0,
          total_amount: budget.total_amount || 0,
          issue_date: budget.issue_date || new Date().toISOString().split('T')[0],
          valid_until: budget.valid_until || '',
          perseo_reference: budget.perseo_reference || '',
          notes: budget.notes || '',
        });
        setItems(budget.items || []);
        setSourceTab(budget.source || 'manual');
      } else {
        form.reset({
          title: '',
          customer_id: '',
          project_id: '',
          status: 'draft',
          source: 'manual',
          subtotal: 0,
          tax_rate: 21,
          tax_amount: 0,
          total_amount: 0,
          issue_date: new Date().toISOString().split('T')[0],
          valid_until: '',
          perseo_reference: '',
          notes: '',
        });
        setItems([]);
        setSourceTab('manual');

        // Fetch next number
        api.from('budgets')
          .select('budget_number')
          .order('budget_number', { ascending: false })
          .limit(1)
          .then(({ data }) => {
            const maxNum = data?.[0]?.budget_number || 0;
            setNextNumber(maxNum + 1);
          });
      }
    }
  }, [open, budget, form]);

  const fetchCustomers = async () => {
    const { data } = await api
      .from('customers')
      .select('id,company_name,codificacion,customer_number')
      .eq('is_active', true)
      .order('company_name');
    setCustomers(data || []);
  };

  const fetchProjects = async () => {
    const { data } = await api
      .from('projects')
      .select('id,name,project_number,customer_id')
      .eq('is_active', true)
      .order('project_number', { ascending: false });
    setProjects(data || []);
  };

  // Filter projects by selected customer
  const filteredProjects = form.watch('customer_id')
    ? projects.filter(p => p.customer_id === form.watch('customer_id'))
    : projects;

  // Calculate totals when items change
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxRate = form.getValues('tax_rate') || 21;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    form.setValue('subtotal', subtotal);
    form.setValue('tax_amount', taxAmount);
    form.setValue('total_amount', total);
  };

  useEffect(() => {
    calculateTotals();
  }, [items]);

  const addItem = () => {
    setItems([
      ...items,
      {
        reference: '',
        id: (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36)),
        description: '',
        quantity: 1,
        unit_price: 0,
        total: 0,
      },
    ]);
  };

  const updateItem = (id: string, field: keyof BudgetItem, value: any) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unit_price') {
          updated.total = (updated.quantity || 0) * (updated.unit_price || 0);
        }
        return updated;
      }
      return item;
    }));
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        source: sourceTab,
        items: items,
        customer_id: data.customer_id || null,
        project_id: data.project_id || null,
        valid_until: data.valid_until || null,
        perseo_reference: data.perseo_reference || null,
        notes: data.notes || null,
      };

      if (budget) {
        const { error } = await api
          .from('budgets')
          .update(payload)
          .eq('id', budget.id);

        if (error) throw error;

        toast({
          title: 'Presupuesto actualizado',
          description: 'El presupuesto se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('budgets')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Presupuesto creado',
          description: 'El presupuesto se creó correctamente',
        });
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el presupuesto',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>{budget ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}</DialogTitle>
            <Badge variant="outline" className="font-mono bg-primary/10">
              PRE-{budget ? String(budget.budget_number).padStart(4, '0') : nextNumber ? String(nextNumber).padStart(4, '0') : '....'}
            </Badge>
          </div>
          {!budget && (
            <DialogDescription>
              Selecciona el origen del presupuesto
            </DialogDescription>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Source Tabs */}
            <Tabs value={sourceTab} onValueChange={setSourceTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="manual" type="button">
                  <FileText className="w-4 h-4 mr-2" />
                  Manual
                </TabsTrigger>
                <TabsTrigger value="perseo" type="button">
                  <Upload className="w-4 h-4 mr-2" />
                  Desde Perseo
                </TabsTrigger>
                <TabsTrigger value="import" type="button">
                  <Upload className="w-4 h-4 mr-2" />
                  Importar
                </TabsTrigger>
              </TabsList>

              <TabsContent value="perseo" className="mt-4">
                <Card className="p-4 bg-muted/50">
                  <FormField
                    control={form.control}
                    name="perseo_reference"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Referencia Perseo</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Ej: COT-2026-0001" />
                        </FormControl>
                        <p className="text-sm text-muted-foreground">
                          Introduce la referencia de la cotización aprobada en Perseo
                        </p>
                      </FormItem>
                    )}
                  />
                </Card>
              </TabsContent>

              <TabsContent value="import" className="mt-4">
                <Card className="p-4 bg-muted/50 text-center">
                  <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Arrastra un archivo Excel o PDF aquí, o haz clic para seleccionar
                  </p>
                  <Button type="button" variant="outline" className="mt-4">
                    Seleccionar archivo
                  </Button>
                </Card>
              </TabsContent>
            {/* Basic Info - moved before manual TabsContent */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Título del presupuesto" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="customer_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar cliente" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            C-{String(c.customer_number).padStart(3, '0')} - {c.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="project_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proyecto</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar proyecto" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filteredProjects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            P-{String(p.project_number).padStart(4, '0')} - {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft">Borrador</SelectItem>
                        <SelectItem value="sent">Enviado</SelectItem>
                        <SelectItem value="approved">Aprobado</SelectItem>
                        <SelectItem value="rejected">Rechazado</SelectItem>
                        <SelectItem value="expired">Expirado</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="issue_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha Emisión</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="valid_until"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Válido Hasta</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Items */}
            <TabsContent value="manual" className="mt-4 space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Líneas del Presupuesto</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir línea
                  </Button>
                </div>

                {items.length === 0 ? (
                  <Card className="p-6 text-center text-muted-foreground">
                    <p>No hay líneas. Haz clic en "Añadir línea" para comenzar.</p>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {items.map((item, index) => (
                      <Card key={item.id} className="p-3">
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-2">
                            <Input
                              placeholder="Referencia"
                              value={item.reference || ""}
                              onChange={(e) => updateItem(item.id, "reference", e.target.value)}
                            />
                          </div>
                          <div className="col-span-3">
                            <Input
                              placeholder="Descripción"
                              value={item.description}
                              onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              placeholder="Cant." className="min-w-[80px] text-right"
                              value={item.quantity}
                              onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Precio" className="min-w-[80px] text-right"
                              value={item.unit_price}
                              onChange={(e) => updateItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                            />
                          </div>
                          <div className="col-span-2 text-right font-semibold">
                            {item.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                          </div>
                          <div className="col-span-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={() => removeItem(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
            </Tabs>

            {/* Totals */}
            <Card className="p-4 bg-muted/30">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span className="font-medium">
                    {form.watch('subtotal').toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">IVA:</span>
                    <Input
                      type="number"
                      className="w-20 h-8"
                      value={form.watch('tax_rate')}
                      onChange={(e) => {
                        form.setValue('tax_rate', parseFloat(e.target.value) || 0);
                        calculateTotals();
                      }}
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                  <span className="font-medium">
                    {form.watch('tax_amount').toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold text-lg">Total:</span>
                  <span className="font-bold text-lg text-primary">
                    {form.watch('total_amount').toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                  </span>
                </div>
              </div>
            </Card>

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales..." rows={3} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {budget ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
